import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { cn } from '../../lib/utils';
import {
  Camera, Upload, FileText, CheckCircle2, AlertTriangle, ArrowRight, Sparkles,
  Shield, Loader2, RefreshCw, User, Search, Activity, Pill, AlertCircle,
  Clipboard, Heart, Stethoscope, ClipboardList, Save,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { useAppMode } from '../../contexts/AppModeContext';

const API = process.env.REACT_APP_BACKEND_URL;

const DOC_TYPE_CONFIG = {
  encounter_note: { label: 'Encounter Note', color: 'bg-blue-50 text-blue-700 border-blue-200', icon: Clipboard },
  discharge_summary: { label: 'Discharge Summary', color: 'bg-indigo-50 text-indigo-700 border-indigo-200', icon: ClipboardList },
  lab_report: { label: 'Lab Report', color: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: Activity },
  referral: { label: 'Referral', color: 'bg-amber-50 text-amber-700 border-amber-200', icon: ArrowRight },
  prescription: { label: 'Prescription', color: 'bg-violet-50 text-violet-700 border-violet-200', icon: Pill },
  other: { label: 'Clinical Document', color: 'bg-slate-100 text-slate-600 border-slate-200', icon: FileText },
};

const SECTION_META = {
  problems: { label: 'Problems', icon: Heart, color: 'text-red-600 bg-red-50' },
  medications: { label: 'Medications', icon: Pill, color: 'text-blue-600 bg-blue-50' },
  allergies: { label: 'Allergies', icon: AlertTriangle, color: 'text-amber-600 bg-amber-50' },
  vitals: { label: 'Vitals', icon: Activity, color: 'text-emerald-600 bg-emerald-50' },
  procedures: { label: 'Procedures', icon: Stethoscope, color: 'text-violet-600 bg-violet-50' },
};

/* ═══════════════════════════════════════════════════════ */

const LuminaLens = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { authFetch } = useAuth();
  const { isDemo } = useAppMode();
  const fileRef = useRef(null);

  // ── Patient context detection ──
  const routePatientId = searchParams.get('patientId') || location.state?.patientId || null;
  const routePatientName = location.state?.patientName || null;
  const [patientCtx, setPatientCtx] = useState({ id: routePatientId, name: routePatientName });

  // ── Pipeline state ──
  const [stage, setStage] = useState('upload'); // upload | processing | review | applied | failed
  const [uploadFile, setUploadFile] = useState(null);
  const [progress, setProgress] = useState(0);
  const [recordId, setRecordId] = useState(null);
  const [extractedData, setExtractedData] = useState(null);
  const [docType, setDocType] = useState('other');
  const [errorMsg, setErrorMsg] = useState('');

  // ── Phase 2: Link Patient dialog ──
  const [linkOpen, setLinkOpen] = useState(false);
  const [patientSearch, setPatientSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const searchTimer = useRef(null);

  // ── Phase 1: Apply to chart ──
  const [applyConfirm, setApplyConfirm] = useState(false);
  const [applying, setApplying] = useState(false);

  // ── Phase 3: Save to My Documents ──
  const [saving, setSaving] = useState(false);
  const [savedToHistory, setSavedToHistory] = useState(false);

  // Resolve patient name if we only have ID
  useEffect(() => {
    if (!routePatientId || routePatientName) return;
    (async () => {
      try {
        const res = await authFetch(`${API}/api/patients/${routePatientId}`);
        if (res.ok) {
          const d = await res.json();
          const name = `${d.first_name || ''} ${d.last_name || ''}`.trim();
          setPatientCtx(prev => ({ ...prev, name: name || prev.id }));
        }
      } catch { /* keep ID only */ }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routePatientId]);

  /* ═══ UPLOAD → EXTRACT PIPELINE ═══ */

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setUploadFile(file);
    setStage('processing');
    setProgress(5);
    setErrorMsg('');
    setExtractedData(null);
    setRecordId(null);
    setSavedToHistory(false);

    try {
      // Step 1: Upload to document pipeline (stores in scan_document_history)
      setProgress(10);
      const form = new FormData();
      form.append('file', file);
      const uploadRes = await authFetch(`${API}/api/patient-documents/upload`, { method: 'POST', body: form });
      if (!uploadRes.ok) throw new Error(`Upload failed (${uploadRes.status})`);
      const uploadData = await uploadRes.json();
      const analysisId = uploadData.analysis_id || uploadData.id;
      setProgress(25);

      // Step 2: Poll vendor processing
      let attempts = 0;
      let completed = false;
      while (attempts < 30 && !completed) {
        await new Promise(r => setTimeout(r, 2000));
        attempts++;
        setProgress(25 + Math.min(attempts * 2, 30));
        try {
          const pollRes = await authFetch(`${API}/api/analyses/${analysisId}`);
          if (pollRes.ok) {
            const pollData = await pollRes.json();
            const s = pollData.status?.toLowerCase();
            if (s === 'completed' || s === 'done') { completed = true; break; }
            if (s === 'failed' || s === 'error') throw new Error('Document processing failed');
          }
        } catch (err) {
          if (err.message === 'Document processing failed') throw err;
        }
      }
      if (!completed) throw new Error('Processing timed out');
      setProgress(60);

      // Step 3: Find record ID in history
      const histRes = await authFetch(`${API}/api/patient-documents/history`);
      if (!histRes.ok) throw new Error('Could not retrieve document history');
      const histData = await histRes.json();
      const matchRec = (histData.records || []).find(r => r.analysis_id === analysisId);
      if (!matchRec?.id) throw new Error('Document record not found after processing');
      setRecordId(matchRec.id);
      setProgress(70);

      // Step 4: GPT-5.2 extraction (no patient_id — review-first safety)
      const extractRes = await authFetch(`${API}/api/patient-documents/history/${matchRec.id}/extract`, { method: 'POST' });
      if (!extractRes.ok) {
        const errData = await extractRes.json().catch(() => ({}));
        throw new Error(errData.detail || `Extraction failed (${extractRes.status})`);
      }
      const extracted = await extractRes.json();
      setProgress(100);

      // Set classification and data
      setExtractedData(extracted);
      setDocType(extracted.document_type || 'other');
      setStage('review');
      toast.success('Document extracted', { description: `Classified as ${(DOC_TYPE_CONFIG[extracted.document_type] || DOC_TYPE_CONFIG.other).label}` });

    } catch (err) {
      setErrorMsg(err.message || 'Processing failed');
      setStage('failed');
      toast.error('Extraction failed', { description: err.message });
    }
  };

  /* ═══ PHASE 1: APPLY TO PATIENT CHART ═══ */

  const handleApplyToChart = async () => {
    if (!recordId || !patientCtx.id) return;
    setApplying(true);
    try {
      // PATCH to link document to patient → triggers absorption batch creation
      const res = await authFetch(`${API}/api/patient-documents/history/${recordId}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ patient_id: patientCtx.id }),
      });
      if (!res.ok) throw new Error('Failed to apply to chart');
      setStage('applied');
      toast.success('Applied to Patient Chart', { description: `Data linked to ${patientCtx.name || patientCtx.id}` });
      setApplyConfirm(false);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setApplying(false);
    }
  };

  /* ═══ PHASE 2: LINK TO PATIENT ═══ */

  const searchPatients = async (term) => {
    if (!term || term.length < 2) { setSearchResults([]); return; }
    setSearching(true);
    try {
      const res = await authFetch(`${API}/api/patients?search=${encodeURIComponent(term)}`);
      if (res.ok) {
        const data = await res.json();
        setSearchResults(data.patients || data.data || []);
      } else { setSearchResults([]); }
    } catch { setSearchResults([]); }
    finally { setSearching(false); }
  };

  const handlePatientSearchInput = (val) => {
    setPatientSearch(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => searchPatients(val), 400);
  };

  const selectPatient = (p) => {
    const pid = p.id || p.patient_id;
    const name = `${p.first_name || ''} ${p.last_name || ''}`.trim();
    setPatientCtx({ id: pid, name: name || pid });
    setLinkOpen(false);
    toast.success(`Patient linked: ${name || pid}`);
  };

  const openLinkDialog = () => {
    const extractedName = extractedData?.patient_info?.name || '';
    setPatientSearch(extractedName);
    setSearchResults([]);
    setLinkOpen(true);
    if (extractedName) searchPatients(extractedName);
  };

  /* ═══ PHASE 3: SAVE TO MY DOCUMENTS ═══ */

  const handleSaveToMyDocs = async () => {
    // The document is already stored in scan_document_history from upload.
    // Just mark it saved and show confirmation.
    setSavedToHistory(true);
    toast.success('Saved to My Documents', { description: 'View in My Documents anytime' });
  };

  /* ═══ PHASE 4: USE IN SOAP NOTE ═══ */

  const handleUseInNote = () => {
    if (extractedData) {
      // Transform extracted data to lens format for SOAP handoff
      const lensData = {
        document_type: docType,
        summary: extractedData.summary,
        sections: [],
      };
      for (const [sec, items] of Object.entries(extractedData)) {
        if (Array.isArray(items) && items.length > 0 && SECTION_META[sec]) {
          lensData.sections.push({
            title: SECTION_META[sec].label,
            items: items.map(item => ({
              key: item.name || item.allergen || item.substance || sec,
              val: item.value || item.dose || item.reaction || item.status || JSON.stringify(item),
            })),
          });
        }
      }
      navigate('/lumina/soap-notes/generate', { state: { lensData } });
    }
  };

  /* ═══ RETRY ═══ */

  const handleRetry = () => {
    setStage('upload');
    setUploadFile(null);
    setProgress(0);
    setRecordId(null);
    setExtractedData(null);
    setErrorMsg('');
    setSavedToHistory(false);
  };

  /* ═══ HELPERS ═══ */

  const sectionCount = (key) => (extractedData?.[key] || []).length;
  const hasData = (key) => sectionCount(key) > 0;
  const dtCfg = DOC_TYPE_CONFIG[docType] || DOC_TYPE_CONFIG.other;
  const DtIcon = dtCfg.icon;

  /* ═══════════════════════════════════════════════════════ */
  /* ═══ RENDER                                          ═══ */
  /* ═══════════════════════════════════════════════════════ */

  return (
    <div className="max-w-5xl mx-auto py-8 px-6 space-y-6" data-testid="lumina-lens-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold font-['Plus_Jakarta_Sans'] text-slate-900">Lumina Lens</h1>
            <Badge className="bg-violet-100 text-violet-700 border-violet-200 text-[10px]">
              <Sparkles className="h-3 w-3 mr-1" /> AI Clinical Extraction
            </Badge>
          </div>
          <p className="text-sm text-slate-500 mt-1">Upload clinical documents for AI-powered extraction and chart integration</p>
        </div>
        <div className="flex items-center gap-2">
          {patientCtx.id && (
            <Badge variant="outline" className="gap-1.5 px-2.5 py-1 text-[11px] font-medium border-blue-200 bg-blue-50 text-blue-700" data-testid="lens-patient-ctx">
              <User className="h-3 w-3" /> {patientCtx.name || patientCtx.id}
            </Badge>
          )}
          <Badge variant="outline" className="gap-1 text-emerald-700 border-emerald-200 bg-emerald-50 text-[10px]">
            <Shield className="h-3 w-3" /> HIPAA Secure
          </Badge>
        </div>
      </div>

      {/* ── UPLOAD STAGE ── */}
      {stage === 'upload' && (
        <>
          <Card>
            <CardHeader>
              <CardTitle className="text-base font-semibold">Upload Clinical Document</CardTitle>
              <CardDescription className="text-xs">Upload encounter notes, lab reports, discharge summaries, or referrals for AI extraction</CardDescription>
            </CardHeader>
            <CardContent>
              <div className={cn(
                "border-2 border-dashed rounded-xl p-12 text-center transition-all cursor-pointer",
                "border-slate-200 hover:border-violet-400 hover:bg-violet-50/30"
              )} onClick={() => fileRef.current?.click()}>
                <div className="h-14 w-14 rounded-full bg-violet-100 flex items-center justify-center mx-auto mb-4">
                  <Camera className="h-7 w-7 text-violet-600" />
                </div>
                <p className="text-base font-medium text-slate-900 mb-1">Drop your file here or click to browse</p>
                <p className="text-xs text-slate-400 mb-4">Supports PDF, PNG, JPG — HIPAA-compliant processing</p>
                <div className="flex gap-3 justify-center">
                  <Button variant="outline" className="gap-2 text-xs" onClick={(e) => { e.stopPropagation(); fileRef.current?.click(); }} data-testid="lens-upload-btn">
                    <Upload className="h-3.5 w-3.5" /> Upload File
                  </Button>
                  <Button className="gap-2 bg-slate-900 hover:bg-slate-800 text-xs" onClick={(e) => { e.stopPropagation(); fileRef.current?.click(); }} data-testid="lens-capture-btn">
                    <Camera className="h-3.5 w-3.5" /> Take Photo
                  </Button>
                </div>
                <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.xml,.json" className="hidden" onChange={handleFileSelect} data-testid="lens-file-input" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-slate-50/50 border-slate-200">
            <CardContent className="p-4">
              <p className="text-xs font-semibold text-slate-600 mb-3">Lens classifies and extracts from:</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {['Encounter Notes', 'Discharge Summaries', 'Lab Reports', 'Referral Letters', 'Prescriptions', 'Problem Lists', 'Medication Lists', 'Imaging Reports'].map(item => (
                  <div key={item} className="flex items-center gap-1.5 text-xs text-slate-500">
                    <CheckCircle2 className="h-3 w-3 text-emerald-500 flex-shrink-0" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* ── PROCESSING STAGE ── */}
      {stage === 'processing' && (
        <Card>
          <CardContent className="p-10">
            <div className="text-center space-y-4">
              <Loader2 className="h-10 w-10 text-violet-600 mx-auto animate-spin" />
              <div>
                <p className="font-medium text-slate-900 text-sm">Processing {uploadFile?.name || 'document'}...</p>
                <p className="text-xs text-slate-400 mt-1">
                  {progress < 25 ? 'Uploading document...' :
                   progress < 60 ? 'Processing with vendor...' :
                   progress < 90 ? 'Extracting clinical data with AI...' :
                   'Finalizing extraction...'}
                </p>
              </div>
              <div className="w-56 mx-auto bg-slate-200 rounded-full h-1.5 overflow-hidden">
                <div className="bg-violet-600 h-full rounded-full transition-all duration-700 ease-out" style={{ width: `${progress}%` }} data-testid="lens-progress-bar" />
              </div>
              <p className="text-[10px] text-slate-400">{progress}%</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── FAILED STAGE ── */}
      {stage === 'failed' && (
        <Card className="border-red-200">
          <CardContent className="p-8 text-center space-y-4">
            <AlertCircle className="h-10 w-10 text-red-500 mx-auto" />
            <p className="font-medium text-slate-900 text-sm">Extraction Failed</p>
            <p className="text-xs text-red-600 max-w-md mx-auto">{errorMsg}</p>
            <div className="flex gap-3 justify-center">
              <Button variant="outline" size="sm" onClick={handleRetry} data-testid="lens-retry-btn">
                <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Try Again
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── REVIEW STAGE (and APPLIED) ── */}
      {(stage === 'review' || stage === 'applied') && extractedData && (
        <>
          {/* Classification banner */}
          <div className="flex items-center justify-between px-4 py-3 rounded-xl border border-slate-200 bg-white" data-testid="lens-classification-banner">
            <div className="flex items-center gap-3">
              <div className={cn("h-9 w-9 rounded-lg flex items-center justify-center", dtCfg.color.split(' ')[0])}>
                <DtIcon className={cn("h-4 w-4", dtCfg.color.split(' ')[1])} />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <Badge className={cn("text-[10px] border", dtCfg.color)} data-testid="lens-doc-type-badge">{dtCfg.label}</Badge>
                  {stage === 'applied' && <Badge className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200 border" data-testid="lens-applied-badge"><CheckCircle2 className="h-2.5 w-2.5 mr-1" />Applied to Chart</Badge>}
                  {savedToHistory && <Badge className="text-[10px] bg-slate-100 text-slate-600 border-slate-200 border"><Save className="h-2.5 w-2.5 mr-1" />Saved</Badge>}
                </div>
                <p className="text-xs text-slate-500 mt-0.5">{uploadFile?.name}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {patientCtx.id ? (
                <Badge variant="outline" className="gap-1 text-[10px] border-blue-200 bg-blue-50 text-blue-700" data-testid="lens-linked-patient">
                  <User className="h-2.5 w-2.5" /> {patientCtx.name || patientCtx.id}
                </Badge>
              ) : (
                <Badge variant="outline" className="gap-1 text-[10px] border-slate-200 text-slate-500" data-testid="lens-no-patient">
                  No patient linked
                </Badge>
              )}
            </div>
          </div>

          {/* Summary */}
          {extractedData.summary && (
            <Card className="border-slate-200">
              <CardContent className="p-4">
                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-1.5">Summary</p>
                <p className="text-xs text-slate-700 leading-relaxed" data-testid="lens-summary">{extractedData.summary}</p>
              </CardContent>
            </Card>
          )}

          {/* Patient Info */}
          {extractedData.patient_info && (extractedData.patient_info.name || extractedData.patient_info.dob) && (
            <Card className="border-slate-200">
              <CardContent className="p-4">
                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-1.5">Patient Information</p>
                <div className="flex gap-4 text-xs text-slate-700">
                  {extractedData.patient_info.name && <span><span className="text-slate-400">Name:</span> {extractedData.patient_info.name}</span>}
                  {extractedData.patient_info.dob && <span><span className="text-slate-400">DOB:</span> {extractedData.patient_info.dob}</span>}
                  {extractedData.patient_info.mrn && <span><span className="text-slate-400">MRN:</span> {extractedData.patient_info.mrn}</span>}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Extracted sections */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="lens-extracted-sections">
            {/* Problems */}
            {hasData('problems') && (
              <Card className="border-slate-200">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Heart className="h-3.5 w-3.5 text-red-500" />
                    <p className="text-[10px] font-semibold text-slate-500 uppercase">Problems ({sectionCount('problems')})</p>
                  </div>
                  <div className="space-y-1">
                    {extractedData.problems.map((p, i) => (
                      <div key={i} className="flex items-center justify-between text-xs py-1 border-b border-slate-50 last:border-0">
                        <span className="text-slate-800 font-medium">{p.name || p}</span>
                        <div className="flex items-center gap-1.5">
                          {(p.icd_code || p.icd) && <Badge variant="outline" className="text-[8px] font-mono">{p.icd_code || p.icd}</Badge>}
                          <Badge className={cn("text-[8px] border-0", p.status === 'resolved' ? 'bg-slate-100 text-slate-500' : 'bg-red-50 text-red-600')}>{p.status || 'active'}</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Medications */}
            {hasData('medications') && (
              <Card className="border-slate-200">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Pill className="h-3.5 w-3.5 text-blue-500" />
                    <p className="text-[10px] font-semibold text-slate-500 uppercase">Medications ({sectionCount('medications')})</p>
                  </div>
                  <div className="space-y-1">
                    {extractedData.medications.map((m, i) => (
                      <div key={i} className="text-xs py-1 border-b border-slate-50 last:border-0">
                        <span className="text-slate-800 font-medium">{m.name || m}</span>
                        {(m.dose || m.frequency) && <span className="text-slate-400 ml-1">— {[m.dose, m.frequency].filter(Boolean).join(', ')}</span>}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Allergies */}
            {hasData('allergies') && (
              <Card className="border-slate-200">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
                    <p className="text-[10px] font-semibold text-slate-500 uppercase">Allergies ({sectionCount('allergies')})</p>
                  </div>
                  <div className="space-y-1">
                    {extractedData.allergies.map((a, i) => (
                      <div key={i} className="flex items-center justify-between text-xs py-1 border-b border-slate-50 last:border-0">
                        <span className="text-slate-800 font-medium">{a.substance || a.name || a}</span>
                        <div className="flex items-center gap-1.5">
                          {a.reaction && <span className="text-slate-400">{a.reaction}</span>}
                          {a.severity && <Badge className={cn("text-[8px] border-0", a.severity === 'severe' ? 'bg-red-100 text-red-700' : a.severity === 'moderate' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600')}>{a.severity}</Badge>}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Vitals */}
            {hasData('vitals') && (
              <Card className="border-slate-200">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Activity className="h-3.5 w-3.5 text-emerald-500" />
                    <p className="text-[10px] font-semibold text-slate-500 uppercase">Vitals ({sectionCount('vitals')})</p>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {extractedData.vitals.map((v, i) => (
                      <div key={i} className="p-2 rounded-lg bg-slate-50 text-xs">
                        <p className="text-slate-400 text-[9px] uppercase">{v.name || v.type}</p>
                        <p className="font-bold text-slate-800 text-sm mt-0.5">{v.value}{v.unit ? ` ${v.unit}` : ''}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Procedures */}
            {hasData('procedures') && (
              <Card className="border-slate-200">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Stethoscope className="h-3.5 w-3.5 text-violet-500" />
                    <p className="text-[10px] font-semibold text-slate-500 uppercase">Procedures ({sectionCount('procedures')})</p>
                  </div>
                  <div className="space-y-1">
                    {extractedData.procedures.map((p, i) => (
                      <div key={i} className="text-xs py-1 border-b border-slate-50 last:border-0">
                        <span className="text-slate-800 font-medium">{p.name || p}</span>
                        {p.date && <span className="text-slate-400 ml-1">— {p.date}</span>}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Assessment & Plan */}
          {extractedData.assessment_plan && (
            <Card className="border-slate-200">
              <CardContent className="p-4">
                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-1.5">Assessment & Plan</p>
                <p className="text-xs text-slate-700 leading-relaxed whitespace-pre-line" data-testid="lens-assessment">{extractedData.assessment_plan}</p>
              </CardContent>
            </Card>
          )}

          {/* ── ACTION BAR ── */}
          <div className="flex items-center justify-between p-4 rounded-xl border border-slate-200 bg-white" data-testid="lens-action-bar">
            <Button variant="outline" size="sm" className="text-xs gap-1.5" onClick={handleRetry} data-testid="lens-upload-another">
              <Upload className="h-3 w-3" /> Upload Another
            </Button>

            <div className="flex items-center gap-2">
              {/* Phase 3: Save to My Documents */}
              {!savedToHistory && (
                <Button variant="outline" size="sm" className="text-xs gap-1.5" onClick={handleSaveToMyDocs} disabled={saving} data-testid="lens-save-mydocs">
                  <Save className="h-3 w-3" /> Save to My Documents
                </Button>
              )}

              {/* Phase 4: Use in SOAP Note */}
              <Button variant="outline" size="sm" className="text-xs gap-1.5" onClick={handleUseInNote} data-testid="lens-use-in-note">
                <FileText className="h-3 w-3" /> Use in SOAP Note
              </Button>

              {/* Phase 2: Link to Patient (when no patient context) */}
              {!patientCtx.id && stage !== 'applied' && (
                <Button size="sm" className="text-xs gap-1.5 bg-blue-600 hover:bg-blue-700" onClick={openLinkDialog} data-testid="lens-link-patient-btn">
                  <User className="h-3 w-3" /> Link to Patient
                </Button>
              )}

              {/* Phase 1: Apply to Patient Chart (when patient context exists) */}
              {patientCtx.id && stage !== 'applied' && (
                <Button size="sm" className="text-xs gap-1.5 bg-emerald-600 hover:bg-emerald-700" onClick={() => setApplyConfirm(true)} data-testid="lens-apply-chart-btn">
                  <CheckCircle2 className="h-3 w-3" /> Apply to Patient Chart
                </Button>
              )}

              {/* Post-apply: navigate to chart */}
              {stage === 'applied' && patientCtx.id && (
                <Button size="sm" className="text-xs gap-1.5 bg-slate-900 hover:bg-slate-800" onClick={() => navigate(`/lumina/clinical-focus?patientId=${patientCtx.id}`)} data-testid="lens-go-to-chart">
                  <ArrowRight className="h-3 w-3" /> View Patient Chart
                </Button>
              )}
            </div>
          </div>
        </>
      )}

      {/* ═══ APPLY CONFIRMATION DIALOG (Phase 1) ═══ */}
      <Dialog open={applyConfirm} onOpenChange={setApplyConfirm}>
        <DialogContent className="sm:max-w-md" data-testid="lens-apply-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="text-sm font-semibold">Apply to Patient Chart?</DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              This will add the extracted clinical data to <strong>{patientCtx.name || patientCtx.id}</strong>'s chart. Problems, medications, allergies, and vitals will appear in the respective chart tabs.
            </DialogDescription>
          </DialogHeader>
          <div className="text-xs text-slate-600 space-y-1 py-2">
            {hasData('problems') && <p>+ {sectionCount('problems')} problem{sectionCount('problems') > 1 ? 's' : ''}</p>}
            {hasData('medications') && <p>+ {sectionCount('medications')} medication{sectionCount('medications') > 1 ? 's' : ''}</p>}
            {hasData('allergies') && <p>+ {sectionCount('allergies')} allerg{sectionCount('allergies') > 1 ? 'ies' : 'y'}</p>}
            {hasData('vitals') && <p>+ {sectionCount('vitals')} vital{sectionCount('vitals') > 1 ? 's' : ''}</p>}
            {hasData('procedures') && <p>+ {sectionCount('procedures')} procedure{sectionCount('procedures') > 1 ? 's' : ''}</p>}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" size="sm" onClick={() => setApplyConfirm(false)} data-testid="lens-apply-cancel">Cancel</Button>
            <Button size="sm" onClick={handleApplyToChart} disabled={applying} className="bg-emerald-600 hover:bg-emerald-700 gap-1.5" data-testid="lens-apply-confirm">
              {applying ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3" />}
              {applying ? 'Applying...' : 'Apply to Chart'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ═══ LINK PATIENT DIALOG (Phase 2) ═══ */}
      <Dialog open={linkOpen} onOpenChange={(v) => { if (!v) setLinkOpen(false); }}>
        <DialogContent className="sm:max-w-md" data-testid="lens-link-dialog">
          <DialogHeader>
            <DialogTitle className="text-sm font-semibold">Link to Patient</DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Search for a patient to link this document and enable chart integration.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
              <input
                type="text"
                placeholder="Search by patient name or MRN..."
                value={patientSearch}
                onChange={(e) => handlePatientSearchInput(e.target.value)}
                className="w-full pl-9 pr-3 py-2.5 rounded-lg border border-slate-200 text-sm outline-none focus:border-slate-400 focus:ring-1 focus:ring-slate-200 transition-colors"
                autoFocus
                data-testid="lens-link-search-input"
              />
              {searching && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400 animate-spin" />}
            </div>
            <div className="max-h-60 overflow-y-auto rounded-lg border border-slate-100" data-testid="lens-link-results">
              {!searching && searchResults.length === 0 && patientSearch.length >= 2 && (
                <div className="px-4 py-6 text-center">
                  <User className="h-5 w-5 text-slate-300 mx-auto mb-2" />
                  <p className="text-xs text-slate-400">No patients found</p>
                </div>
              )}
              {!searching && patientSearch.length < 2 && (
                <div className="px-4 py-6 text-center">
                  <p className="text-xs text-slate-400">Type at least 2 characters to search</p>
                </div>
              )}
              {searchResults.map((p, idx) => {
                const name = `${p.first_name || ''} ${p.last_name || ''}`.trim() || 'Unknown';
                const pid = p.id || p.patient_id || '';
                const dob = p.date_of_birth || p.dob || '';
                return (
                  <button
                    key={pid || idx}
                    onClick={() => selectPatient(p)}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-50 transition-colors border-b border-slate-50 last:border-b-0"
                    style={{ background: 'none', border: 'none', cursor: 'pointer', borderBottom: '1px solid #f8fafc' }}
                    data-testid={`lens-link-option-${idx}`}
                  >
                    <div className="flex-shrink-0 h-8 w-8 rounded-full bg-blue-50 flex items-center justify-center">
                      <User className="h-3.5 w-3.5 text-blue-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-800 truncate">{name}</p>
                      <p className="text-[10px] text-slate-400">
                        {pid && <span>ID: {pid}</span>}
                        {dob && <span className="ml-2">DOB: {dob}</span>}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LuminaLens;
